import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail } from "lucide-react";

const PrivacidadePage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12 bg-muted/30">
        <div className="container max-w-4xl space-y-8">
          
          {/* Card 1 - Política de Privacidade */}
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl font-display">Política de Privacidade</CardTitle>
              <p className="text-muted-foreground">Última atualização: Janeiro de 2026</p>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none space-y-6">
              <p className="text-muted-foreground">
                A <strong>JobwayX</strong> valoriza a privacidade e a proteção dos dados pessoais de seus usuários. Esta Política de Privacidade foi elaborada em conformidade com a <strong>Lei nº 13.709/2018 – Lei Geral de Proteção de Dados (LGPD)</strong> e demais normas aplicáveis no Brasil, descrevendo de forma clara como coletamos, utilizamos, armazenamos e protegemos seus dados pessoais.
              </p>
              <p className="text-muted-foreground">
                Ao utilizar a plataforma JobwayX, você declara estar ciente e de acordo com as práticas descritas nesta Política.
              </p>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">1. Dados Pessoais que Coletamos</h2>
                <p className="text-muted-foreground mb-2">
                  Podemos coletar os seguintes dados pessoais, de acordo com a sua interação com a plataforma:
                </p>
                
                <h3 className="text-lg font-medium mb-2 mt-4">1.1 Dados fornecidos diretamente pelo usuário</h3>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Nome completo</li>
                  <li>Endereço de e-mail</li>
                  <li>Número de telefone</li>
                  <li>Endereço</li>
                  <li>CPF ou outros dados de identificação, quando necessários</li>
                  <li>Informações profissionais (currículo, experiências, qualificações, formação acadêmica)</li>
                </ul>

                <h3 className="text-lg font-medium mb-2 mt-4">1.2 Dados coletados automaticamente</h3>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Endereço IP</li>
                  <li>Tipo de dispositivo e navegador</li>
                  <li>Páginas acessadas e tempo de navegação</li>
                  <li>Cookies e tecnologias similares</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">2. Finalidade do Tratamento dos Dados</h2>
                <p className="text-muted-foreground mb-2">
                  Os dados pessoais coletados são utilizados para as seguintes finalidades:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Viabilizar o cadastro e autenticação de usuários na plataforma</li>
                  <li>Facilitar a conexão entre candidatos e empresas</li>
                  <li>Permitir a candidatura a vagas de emprego</li>
                  <li>Melhorar continuamente a experiência do usuário e os serviços oferecidos</li>
                  <li>Enviar comunicações, alertas e notificações sobre vagas relevantes</li>
                  <li>Cumprir obrigações legais e regulatórias</li>
                  <li>Prevenir fraudes e garantir a segurança da plataforma</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">3. Base Legal para o Tratamento</h2>
                <p className="text-muted-foreground mb-2">
                  O tratamento de dados pessoais pela JobwayX ocorre com fundamento nas seguintes bases legais previstas na LGPD:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Consentimento do titular</li>
                  <li>Execução de contrato ou de procedimentos preliminares</li>
                  <li>Cumprimento de obrigação legal ou regulatória</li>
                  <li>Legítimo interesse, respeitados os direitos e liberdades fundamentais do titular</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">4. Compartilhamento de Dados</h2>
                <p className="text-muted-foreground mb-2">
                  A JobwayX <strong>não comercializa dados pessoais</strong>. O compartilhamento ocorre apenas quando necessário e nos seguintes casos:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Com empresas contratantes, exclusivamente quando o usuário se candidata a uma vaga</li>
                  <li>Com fornecedores e prestadores de serviços, sob contratos que asseguram confidencialidade e proteção dos dados</li>
                  <li>Com autoridades públicas, quando exigido por lei, ordem judicial ou requisição de órgão competente</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">5. Armazenamento e Segurança dos Dados</h2>
                <p className="text-muted-foreground mb-2">
                  Adotamos medidas técnicas e organizacionais adequadas para proteger os dados pessoais contra acessos não autorizados, perda, destruição, alteração ou divulgação indevida, incluindo:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Controle de acesso restrito</li>
                  <li>Criptografia e protocolos de segurança</li>
                  <li>Monitoramento contínuo de sistemas</li>
                </ul>
                <p className="text-muted-foreground mt-2">
                  Os dados serão armazenados apenas pelo tempo necessário para cumprir as finalidades para as quais foram coletados, respeitando obrigações legais e regulatórias.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">6. Direitos do Titular dos Dados</h2>
                <p className="text-muted-foreground mb-2">
                  Nos termos da LGPD, o titular dos dados pode, a qualquer momento, solicitar:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Confirmação da existência de tratamento</li>
                  <li>Acesso aos seus dados pessoais</li>
                  <li>Correção de dados incompletos, inexatos ou desatualizados</li>
                  <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos</li>
                  <li>Portabilidade dos dados, quando aplicável</li>
                  <li>Revogação do consentimento</li>
                  <li>Informação sobre compartilhamento de dados</li>
                  <li>Oposição ao tratamento realizado com base no legítimo interesse</li>
                </ul>
                <p className="text-muted-foreground mt-2">
                  As solicitações serão atendidas dentro dos prazos legais.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">7. Uso de Cookies</h2>
                <p className="text-muted-foreground">
                  Utilizamos cookies e tecnologias semelhantes para melhorar a navegação e personalizar a experiência do usuário. O usuário pode, a qualquer momento, gerenciar ou desabilitar os cookies por meio das configurações do seu navegador, ciente de que isso pode impactar algumas funcionalidades da plataforma.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">8. Atualizações desta Política</h2>
                <p className="text-muted-foreground">
                  Esta Política de Privacidade poderá ser atualizada periodicamente para refletir melhorias na plataforma ou alterações legais. Recomendamos que o usuário revise este documento regularmente.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">9. Canal de Contato</h2>
                <p className="text-muted-foreground mb-2">
                  Para dúvidas, solicitações ou reclamações relacionadas à privacidade e proteção de dados pessoais, entre em contato:
                </p>
                <a 
                  href="mailto:contato@jobwayx.com.br" 
                  className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                >
                  <Mail className="h-4 w-4" />
                  contato@jobwayx.com.br
                </a>
                <p className="text-muted-foreground mt-4">
                  A JobwayX está comprometida com a transparência, segurança e respeito aos direitos dos titulares de dados.
                </p>
              </section>
            </CardContent>
          </Card>

          {/* Card 2 - Política de Cookies */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-display">Política de Cookies</CardTitle>
              <p className="text-muted-foreground">Última atualização: Janeiro de 2026</p>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none space-y-6">
              <p className="text-muted-foreground">
                A JobwayX utiliza cookies e tecnologias semelhantes para garantir o correto funcionamento da plataforma, melhorar a experiência do usuário e oferecer conteúdos personalizados.
              </p>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">1. O que são Cookies</h2>
                <p className="text-muted-foreground">
                  Cookies são pequenos arquivos armazenados no seu dispositivo quando você acessa um site, permitindo reconhecer preferências e melhorar a navegação.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">2. Tipos de Cookies Utilizados</h2>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li><strong>Cookies necessários:</strong> essenciais para o funcionamento da plataforma</li>
                  <li><strong>Cookies de desempenho:</strong> coletam informações sobre uso e navegação</li>
                  <li><strong>Cookies de funcionalidade:</strong> armazenam preferências do usuário</li>
                  <li><strong>Cookies de marketing:</strong> utilizados para comunicação e campanhas relevantes</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">3. Gerenciamento de Cookies</h2>
                <p className="text-muted-foreground">
                  O usuário pode gerenciar ou desativar cookies diretamente nas configurações do navegador, ciente de que algumas funcionalidades poderão ser limitadas.
                </p>
              </section>
            </CardContent>
          </Card>

          {/* Card 3 - Adaptação para Aplicativo Mobile - LGPD */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-display">Adaptação para Aplicativo Mobile</CardTitle>
              <p className="text-muted-foreground">Última atualização: Janeiro de 2026</p>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none space-y-6">
              <p className="text-muted-foreground">
                Esta Política e os Termos de Uso aplicam-se igualmente ao uso da JobwayX em aplicativos móveis (Android e iOS).
              </p>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">1. Dados Coletados no Ambiente Mobile</h2>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Identificadores do dispositivo</li>
                  <li>Dados de uso do aplicativo</li>
                  <li>Permissões concedidas pelo usuário</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">2. Permissões</h2>
                <p className="text-muted-foreground">
                  O aplicativo solicitará apenas permissões estritamente necessárias ao seu funcionamento, sempre com transparência.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">3. Notificações Push</h2>
                <p className="text-muted-foreground">
                  O usuário poderá autorizar ou revogar o recebimento de notificações a qualquer momento nas configurações do dispositivo.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-display font-semibold mb-3">4. Segurança</h2>
                <p className="text-muted-foreground">
                  A JobwayX adota medidas específicas para proteção de dados em ambientes móveis, incluindo criptografia e autenticação segura.
                </p>
              </section>

              <hr className="my-6 border-border" />
              
              <p className="text-muted-foreground font-medium">
                A JobwayX reafirma seu compromisso com a conformidade legal, a transparência e a proteção de dados em todos os seus canais digitais.
              </p>
            </CardContent>
          </Card>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacidadePage;
